"use client";

import * as React from "react";
import * as DialogPrimitive from "@radix-ui/react-dialog";
import { cn } from "@/lib/utils";
import { motion, AnimatePresence } from "framer-motion";

const Dialog = DialogPrimitive.Root;
const DialogTrigger = DialogPrimitive.Trigger;
const DialogPortal = DialogPrimitive.Portal;
const DialogClose = DialogPrimitive.Close;

const overlayVariants = {
  hidden: { opacity: 0 },
  visible: { opacity: 1 },
};

const contentVariants = {
  hidden: {
    opacity: 0,
    scale: 0.95,
  },
  visible: {
    opacity: 1,
    scale: 1,
    transition: {
      type: "spring",
      damping: 25,
      stiffness: 300,
    },
  },
  exit: {
    opacity: 0,
    scale: 0.95,
    transition: {
      duration: 0.2,
    },
  },
};

const DialogOverlay = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Overlay>,
  Omit<
    React.ComponentPropsWithoutRef<typeof DialogPrimitive.Overlay>,
    "asChild"
  >
>(({ className, ...props }, ref) => (
  <DialogPrimitive.Overlay ref={ref} asChild>
    <motion.div
      initial="hidden"
      animate="visible"
      exit="hidden"
      variants={overlayVariants}
      transition={{ duration: 0.2 }}
      className={cn("fixed inset-0 z-[1100] bg-black/30 ", className)}
      {...(props as any)}
    />
  </DialogPrimitive.Overlay>
));
DialogOverlay.displayName = DialogPrimitive.Overlay.displayName;

interface DialogContentProps extends Omit<
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Content>,
  "asChild"
> {
  open?: boolean;
  cleanupMode?: "aggressive" | "scoped";
  overlayClassName?: string;
}

const DialogContent = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Content>,
  DialogContentProps
>(
  (
    {
      className,
      children,
      open,
      cleanupMode = "scoped",
      overlayClassName,
      onPointerDownOutside,
      onEscapeKeyDown,
      ...props
    },
    ref,
  ) => {
    const handleKeyDownCapture = React.useCallback(
      (e: React.KeyboardEvent<HTMLElement>) => {
        // Consumers may attach their own handler
        const consumerHandler = (props as any)?.onKeyDownCapture as
          | ((event: React.KeyboardEvent<HTMLElement>) => void)
          | undefined;
        consumerHandler?.(e);

        // Only handle plain Enter
        if (
          e.defaultPrevented ||
          e.key !== "Enter" ||
          e.shiftKey ||
          e.altKey ||
          e.ctrlKey ||
          e.metaKey ||
          (e as any).isComposing
        ) {
          return;
        }

        // If user is typing into an input/textarea/select/contenteditable, don't hijack Enter
        const target = e.target as HTMLElement | null;
        if (target) {
          const tag = target.tagName;
          if (tag === "TEXTAREA" || tag === "SELECT") return;
          if (tag === "INPUT") {
            const input = target as HTMLInputElement;
            const type = (input.type || "").toLowerCase();
            // Allow Enter on button-ish inputs only
            if (!["button", "submit", "reset"].includes(type)) return;
          }
          if (target.isContentEditable) return;
        }

        // Opt-in default action inside dialog
        const root = e.currentTarget as HTMLElement;
        const defaultAction = root.querySelector<HTMLElement>(
          '[data-dialog-default-action="true"]',
        );

        if (!defaultAction) return;

        // Respect disabled states
        const isDisabled =
          (defaultAction as any).disabled === true ||
          defaultAction.getAttribute("aria-disabled") === "true";
        if (isDisabled) return;

        e.preventDefault();
        e.stopPropagation();
        defaultAction.click();
      },
      [props],
    );

    // Global cleanup for Radix UI Dialog side effects
    React.useEffect(() => {
      if (!open) {
        const cleanup = setTimeout(() => {
          // Always reset body-level styles after close.
          document.body.style.pointerEvents = "";
          document.body.style.overflow = "";
          document.body.style.removeProperty("pointer-events");
          document.body.style.removeProperty("overflow");

          if (cleanupMode === "aggressive") {
            const hasOpenDialog = !!document.querySelector(
              '[role="dialog"][data-state="open"]',
            );
            if (!hasOpenDialog) {
              document
                .querySelectorAll("[data-radix-dialog-overlay]")
                .forEach((el) => {
                  el.remove();
                });
            }
          }

          // Shared safe cleanup: remove empty portals only.
          document
            .querySelectorAll("[data-radix-portal]")
            .forEach((portal) => {
              if (!portal.hasChildNodes()) {
                portal.remove();
              }
            });
        }, 100);

        return () => clearTimeout(cleanup);
      }
    }, [open, cleanupMode]);

    return (
      <DialogPortal>
        <AnimatePresence mode="wait">
          {open && (
            <>
              <DialogOverlay className={overlayClassName} />
              <div
                className={cn(
                  "fixed inset-0 z-[1100] flex",
                  className?.includes("search-dialog") ||
                    className?.includes("palette-dialog")
                    ? "items-start pt-[80px] md:pt-[20vh]"
                    : "items-center",
                  "justify-center px-4 pointer-events-none",
                )}
              >
                <DialogPrimitive.Content
                  ref={ref}
                  asChild
                  onPointerDownOutside={onPointerDownOutside}
                  onEscapeKeyDown={onEscapeKeyDown}
                >
                  <motion.div
                    initial="hidden"
                    animate="visible"
                    exit="exit"
                    variants={contentVariants}
                    className={cn(
                      "w-full pointer-events-auto",
                      className?.includes("search-dialog") ||
                        className?.includes("palette-dialog")
                        ? "max-w-2xl"
                        : "max-w-full w-[100%] sm:w-[90%]",
                      "rounded-3xl shadow-lg",
                      // Use dynamic viewport height on mobile for safe sizing
                      // and keep desktop a bit smaller for aesthetics
                      "max-h-[92dvh] md:max-h-[85vh] overflow-hidden",
                      "bg-background dark:bg-gray-900",
                      // "backdrop-blur-xl backdrop-saturate-150",
                      "relative",
                      "after:absolute after:inset-0",
                      "after:bg-gradient-to-b after:from-white/5 after:to-transparent after:pointer-events-none",
                      "dark:after:from-black/5 dark:after:to-transparent",
                      "border border-white/20 dark:border-gray-800/20",
                      className,
                    )}
                    // Enable smooth touch scrolling on iOS within scrollable children
                    style={{
                      WebkitOverflowScrolling: "touch",
                      ...(props as any)?.style,
                    }}
                    onDragStart={(e) => e.preventDefault()}
                    onKeyDownCapture={handleKeyDownCapture}
                    {...(props as any)}
                  >
                    {children}
                  </motion.div>
                </DialogPrimitive.Content>
              </div>
            </>
          )}
        </AnimatePresence>
      </DialogPortal>
    );
  },
);
DialogContent.displayName = DialogPrimitive.Content.displayName;

const DialogHeader = ({
  className,
  ...props
}: React.HTMLAttributes<HTMLDivElement>) => (
  <div
    className={cn(
      "flex flex-col space-y-1.5 text-center sm:text-left",
      className,
    )}
    {...props}
  />
);
DialogHeader.displayName = "DialogHeader";

const DialogFooter = ({
  className,
  ...props
}: React.HTMLAttributes<HTMLDivElement>) => (
  <div
    className={cn(
      "flex flex-col-reverse sm:flex-row sm:justify-end sm:space-x-2 gap-2",
      className,
    )}
    {...props}
  />
);
DialogFooter.displayName = "DialogFooter";

const DialogTitle = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Title>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Title>
>(({ className, ...props }, ref) => (
  <DialogPrimitive.Title
    ref={ref}
    className={cn(
      "text-lg font-semibold leading-none tracking-tight",
      className,
    )}
    {...props}
  />
));
DialogTitle.displayName = DialogPrimitive.Title.displayName;

const DialogDescription = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Description>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Description>
>(({ className, ...props }, ref) => (
  <DialogPrimitive.Description
    ref={ref}
    className={cn("text-sm text-muted-foreground", className)}
    {...props}
  />
));
DialogDescription.displayName = DialogPrimitive.Description.displayName;

export {
  Dialog,
  DialogPortal,
  DialogOverlay,
  DialogTrigger,
  DialogClose,
  DialogContent,
  DialogHeader,
  DialogFooter,
  DialogTitle,
  DialogDescription,
};
